import React from 'react';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { Box, Button, ButtonBase, IconButton, Paper, Typography } from '@mui/material';
import { colors } from './analysis.shared';
import type { NutritionalAnalysisMicrosView } from './mergeMealPlanNutritionalAnalysis';

type MicronutrientStatus = 'adequate' | 'low' | 'excess' | 'toxic';
type MicronutrientGroup = 'vitamins' | 'minerals' | 'fatty_acids';

interface MicronutrientBar {
  id: string;
  label: string;
  percent: number;
  status: MicronutrientStatus;
  /** When omitted (e.g. custom `items`), category is inferred from `label`. */
  group?: MicronutrientGroup;
  intakeValue?: number;
  intakeUnit?: string;
  rdaValue?: number;
  ulValue?: number;
  toxicityValue?: number;
  percentOfUl?: number;
  insight?: string;
  foodSources?: string[];
}

const GROUP_CATEGORY_LABEL: Record<MicronutrientGroup, string> = {
  vitamins: 'Vitamins',
  minerals: 'Minerals',
  fatty_acids: 'Fatty acids',
};

const INCLUDED_MICRO_IDS = new Set([
  'vitamin_a',
  'vitamin_d',
  'vitamin_e',
  'vitamin_k',
  'vitamin_k1',
  'vitamin_b1',
  'vitamin_b2',
  'vitamin_b3',
  'vitamin_b5',
  'vitamin_b6',
  'folate',
  'vitamin_b12',
  'biotin',
  'vitamin_c',
  'sodium',
  'potassium',
  'chloride',
  'calcium',
  'phosphorus',
  'magnesium',
  'iron',
  'zinc',
  'copper',
  'manganese',
  'selenium',
  'iodine',
  'mufa',
  'monounsaturated_fat',
  'pufa',
  'polyunsaturated_fat',
  'omega_3',
  'omega_6',
  'ala',
  'la',
  'dha',
  'epa',
]);

function normalizeNutrientIdKey(id: string): string {
  return id.trim().toLowerCase().replace(/\s+/g, '_');
}

/** Infer vitamin vs mineral from display name when API does not send a `group` (Figma chip). */
function inferMicronutrientGroup(label: string): MicronutrientGroup {
  const l = label.trim().toLowerCase();
  if (/\bmufa\b|\bpufa\b|omega|monounsaturated|polyunsaturated|\bala\b|\bla\b|\bdha\b|\bepa\b|fatty acid/.test(l)) {
    return 'fatty_acids';
  }
  const isVitamin =
    /vitamin\s*a\b|vitamin\s*d\b|vitamin\s*e\b|vitamin\s*k(?:1|2|₁|₂)?\b|retinol|cholecalciferol|ergocalciferol|tocopherol|phylloquinone|menaquinone/.test(
      l,
    ) ||
    /vitamin\s*c\b|vitamin\s*b|thiamin|riboflavin|niacin|b6|b12|folate|folic|biotin|pantothenic|pyridoxine|cobalamin/.test(l);
  return isVitamin ? 'vitamins' : 'minerals';
}

function statusHeadline(status: MicronutrientStatus): string {
  if (status === 'adequate') return 'Adequate';
  if (status === 'excess') return 'Excess';
  if (status === 'toxic') return 'Toxic';
  return 'Low';
}

interface MicronutrientsRdaSectionProps {
  items?: MicronutrientBar[];
  microsView?: NutritionalAnalysisMicrosView | null;
}

const filterOrder = ['all', 'toxic', 'excess', 'low', 'adequate'] as const;
type FilterKey = (typeof filterOrder)[number];

function getStatusColor(status: MicronutrientStatus): string {
  if (status === 'toxic') return '#EF6D7A';
  if (status === 'excess') return '#D92D20';
  if (status === 'low') return colors.amber;
  return '#2ec3b6';
}

function resolvedGroup(bar: MicronutrientBar): MicronutrientGroup {
  return bar.group ?? inferMicronutrientGroup(bar.label);
}

/** Figma 97:2262 — intake scale track. */
const SCALE_GRADIENT =
  'linear-gradient(90deg, rgb(254, 235, 209) 0%, rgb(214, 251, 229) 31.25%, rgb(210, 250, 229) 63.549%, rgb(254, 247, 233) 75.061%, rgb(254, 199, 199) 100%)';

const RDA_MARKER_PCT = 29.8;
const UL_MARKER_PCT = 76.3;
const TOX_MARKER_PCT = 95;

function formatAmount(value: number | undefined, unit: string): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const u = (unit || 'mg').trim();
  const rounded = Math.abs(value - Math.round(value)) < 0.01 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded.toLocaleString('en-US')}${u ? ` ${u}` : ''}`;
}

function defaultInsight(status: MicronutrientStatus, label: string): string {
  if (status === 'adequate') {
    return `${label} intake is in an adequate range for this plan relative to the reference target.`;
  }
  if (status === 'excess') {
    return `${label} exceeds the configured intake target but remains below the upper limit.`;
  }
  if (status === 'toxic') {
    return `${label} may be approaching or exceeding safe upper limits. Interpret alongside clinical guidance.`;
  }
  return `${label} is below the reference target for this period. Consider dietary sources or plan adjustments.`;
}

function pickNiceTickStep(maxValue: number, maxTickCount = 6): number {
  if (maxValue <= 0) return 50;
  const rough = maxValue / Math.max(1, maxTickCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(rough, 1))));
  const normalized = rough / magnitude;
  const niceUnit =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
  return niceUnit * magnitude;
}

function buildYAxisTicks(scaleMax: number, maxTickCount = 6): number[] {
  if (!Number.isFinite(scaleMax) || scaleMax <= 0) return [0];

  const step = pickNiceTickStep(scaleMax, maxTickCount);
  const ticks: number[] = [0];

  for (let tick = step; tick < scaleMax; tick += step) {
    ticks.push(Math.round(tick));
    if (ticks.length >= maxTickCount - 1) break;
  }

  const last = ticks[ticks.length - 1];
  if (last < scaleMax) {
    ticks.push(Math.round(scaleMax));
  }

  return ticks;
}

function normalizeBarPercent(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
}

function computeChartScaleMax(percentValues: number[]): number {
  const finite = percentValues.filter((value) => Number.isFinite(value) && value >= 0);
  if (!finite.length) return 200;

  const max = Math.max(...finite);
  if (max <= 200) return 200;

  const expanded = Math.ceil(max / 50) * 50;
  // Keep axis readable when one nutrient has an extreme % of RDA.
  return Math.min(expanded, 1000);
}

function MicronutrientDetailPanel({ bar }: { bar: MicronutrientBar }) {
  const pct = Math.max(0, Math.min(200, bar.percent));
  const thumbLeftPct = (pct / 200) * 100;
  const unit = bar.intakeUnit ?? 'mg';
  const intake = bar.intakeValue;
  const rda = bar.rdaValue;
  const ul = bar.ulValue;
  const insight = bar.insight?.trim() || defaultInsight(bar.status, bar.label);
  const foods = bar.foodSources ?? [];
  const letter = bar.label.trim().charAt(0).toUpperCase() || '?';

  const ulSub = null;
  const keyValueRows = [
    { k: 'intake', label: 'Current Intake', value: formatAmount(intake, unit), valueColor: '#2ec3b6', sub: null as string | null },
    {
      k: 'rda',
      label: 'RDA',
      value: formatAmount(rda, unit),
      valueColor: '#2ec3b6',
      sub: 'Recommended Dietary Allowance',
    },
    {
      k: 'ul',
      label: 'Upper Limit (UL)',
      value: formatAmount(ul, unit),
      valueColor: '#f59e0b',
      sub: ulSub,
    },
  ];
  const leftColumnRows = keyValueRows.slice(0, 2);
  const rightColumnRows = keyValueRows.slice(2);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
  
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'stretch', width: '100%' }}>
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '12px',
              lineHeight: '13.5px',
              letterSpacing: '1.067px',
              textTransform: 'uppercase',
              color: '#3a3a3a',
              mb: 1,
            }}
          >
            Key Values
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '30px', alignItems: 'stretch' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
            {leftColumnRows.map((row) => (
              <Box
                key={row.k}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: row.sub ? 'flex-start' : 'center',
                  gap: 2,
                  py: '12px',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#6a7282' }}>{row.label}</Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '12px', color: row.valueColor }}>
                    {row.value}
                  </Typography>
                  {row.sub ? (
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#6a7282', mt: 0.25, lineHeight: 1.4 }}>
                      {row.sub}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            ))}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {rightColumnRows.map((row) => (
                <Box
                  key={row.k}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: row.sub ? 'flex-start' : 'center',
                    gap: 2,
                    py: '12px',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#6a7282' }}>{row.label}</Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '12px', color: row.valueColor }}>
                      {row.value}
                    </Typography>
                    {row.sub ? (
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#6a7282', mt: 0.25, lineHeight: 1.4 }}>
                        {row.sub}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Temporarily hidden per request: Clinical Insights + Best Food Sources cards */}
        {/* <Box sx={{ width: '100%' }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '12px',
              lineHeight: '13.5px',
              letterSpacing: '1.067px',
              textTransform: 'uppercase',
              color: '#3a3a3a',
              mb: 1,
            }}
          >
            Nutrient Insights
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '40px', width: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  border: '1px solid #f3f4f6',
                  borderRadius: '16px',
                  bgcolor: '#fff',
                  p: '17px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '11px',
                  minHeight: 120,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AutoAwesomeRounded sx={{ fontSize: 15, color: '#20aedc' }} />
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '12px', color: '#20aedc' }}>
                    Clinical Insights
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', lineHeight: '19.5px', color: '#374151' }}>{insight}</Typography>
              </Box>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  border: '1px solid #f3f4f6',
                  borderRadius: '16px',
                  bgcolor: '#fff',
                  p: '17px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '11px',
                  minHeight: 120,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AutoAwesomeRounded sx={{ fontSize: 15, color: '#20aedc' }} />
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '12px', color: '#20aedc' }}>
                    Best Food Sources
                  </Typography>
                </Box>
                {foods.length ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {foods.map((name) => (
                      <Box
                        key={name}
                        sx={{
                          bgcolor: '#f3f4f6',
                          borderRadius: 9999,
                          px: '10px',
                          py: '6px',
                          height: 27,
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            fontSize: '12px',
                            color: '#4a5565',
                            letterSpacing: '0.1172px',
                          }}
                        >
                          {name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#94a3b8' }}>
                    Food sources appear when provided by your meal plan analysis.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box> */}
      </Box>
    </Box>
  );
}

export const MicronutrientsRdaSection: React.FC<MicronutrientsRdaSectionProps> = ({ items, microsView }) => {
  const [activeFilter, setActiveFilter] = React.useState<FilterKey>('all');
  const [selectedBar, setSelectedBar] = React.useState<MicronutrientBar | null>(null);
  const detailPanelRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    setSelectedBar(null);
  }, [items, microsView]);

  const barsFromMicros = React.useMemo<MicronutrientBar[]>(() => {
    const all = microsView?.byStatus?.all ?? [];
    return all
      .filter((row) => INCLUDED_MICRO_IDS.has(normalizeNutrientIdKey(row.nutrientId)))
      .map((row) => ({
        id: row.nutrientId,
        label: row.displayName,
        percent: row.percentOfTarget,
        status: row.status === 'unknown' ? 'low' : row.status,
        group: inferMicronutrientGroup(row.displayName),
        intakeValue: row.intakeValue,
        intakeUnit: row.intakeUnit,
        rdaValue: row.rdaValue,
        ulValue: row.ulValue,
        toxicityValue: row.toxicityValue,
        percentOfUl: row.percentOfUl,
        insight: row.insight,
        foodSources: row.foodSources,
      }));
  }, [microsView]);

  const bars = items?.length
    ? items.map((b) => ({ ...b, group: b.group ?? inferMicronutrientGroup(b.label) }))
    : barsFromMicros.length
      ? barsFromMicros
      : [];

  const counts = React.useMemo(() => {
    return {
      all: bars.length,
      toxic: bars.filter((item) => item.status === 'toxic').length,
      excess: bars.filter((item) => item.status === 'excess').length,
      low: bars.filter((item) => item.status === 'low').length,
      adequate: bars.filter((item) => item.status === 'adequate').length,
    };
  }, [bars, microsView]);
  const categoryCount = React.useMemo(
    () => new Set(bars.map((bar) => resolvedGroup(bar))).size,
    [bars],
  );

  const visibleBars = React.useMemo(
    () => (activeFilter === 'all' ? bars : bars.filter((item) => item.status === activeFilter)),
    [activeFilter, bars]
  );

  React.useEffect(() => {
    if (!selectedBar) return;
    const stillVisible = visibleBars.some((bar) => bar.id === selectedBar.id);
    if (!stillVisible) setSelectedBar(null);
  }, [selectedBar, visibleBars]);

  React.useEffect(() => {
    if (!selectedBar || !detailPanelRef.current) return;
    detailPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedBar]);

  const chartHeight = 170;
  const chartBottom = 52;
  const yAxisWidth = 48;
  const longestLabelChars = visibleBars.reduce((max, bar) => Math.max(max, bar.label.trim().length), 0);
  const perBarMinWidth = Math.max(44, Math.min(120, longestLabelChars * 6));
  const chartMinWidth = Math.max(560, visibleBars.length * perBarMinWidth);
  const columns = `repeat(${Math.max(1, visibleBars.length)}, minmax(${perBarMinWidth}px, 1fr))`;
  const normalizedPercents = visibleBars.map((bar) => normalizeBarPercent(bar.percent));
  const scaleMax = computeChartScaleMax(normalizedPercents);
  const yTicks = buildYAxisTicks(scaleMax);
  const plotHeight = chartHeight + chartBottom;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid #f3f4f6',
        bgcolor: '#fff',
        boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px -1px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        minWidth: 0,
        p: '1px',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0, 1fr) auto',
          },
          alignItems: { lg: 'start' },
          pt: '20px',
          pb: '16px',
          px: '24px',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '24px',
              letterSpacing: '-0.3125px',
              color: '#1e2939',
            }}
          >
            Micronutrients vs RDA
          </Typography>
          <Typography
            sx={{
              mt: '2px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              lineHeight: '16px',
              color: '#99a1af',
              overflowWrap: 'anywhere',
            }}
          >
            {counts.all} nutrients · {categoryCount} {categoryCount === 1 ? 'category' : 'categories'} ·{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#99a1af' }}>
              click any bar for detail
            </Box>
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            justifyContent: { lg: 'flex-end' },
            minWidth: 0,
          }}
        >
          {filterOrder.map((filterKey) => {
            const selected = activeFilter === filterKey;
            const label = filterKey === 'all' ? 'All' : filterKey.charAt(0).toUpperCase() + filterKey.slice(1);
            const countColor =
              filterKey === 'toxic' || filterKey === 'excess' ? '#ef4444' : filterKey === 'low' ? '#f59e0b' : filterKey === 'adequate' ? '#10b981' : '#667085';
            const filterIcon =
              filterKey === 'toxic' || filterKey === 'excess' ? (
                <WarningAmberRounded sx={{ fontSize: 10, color: selected ? '#fff' : '#ef4444' }} />
              ) : filterKey === 'low' ? (
                <WarningAmberRounded sx={{ fontSize: 10, color: selected ? '#fff' : '#f59e0b' }} />
              ) : filterKey === 'adequate' ? (
                <CheckCircleRounded sx={{ fontSize: 10, color: selected ? '#fff' : '#10b981' }} />
              ) : null;

            return (
              <Button
                key={filterKey}
                type="button"
                onClick={() => {
                  setActiveFilter(filterKey);
                }}
                sx={{
                  minHeight: 30,
                  height: 30,
                  minWidth: 70,
                  borderRadius: '999px',
                  px: '12px',
                  py: 0,
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '16px',
                  bgcolor: selected ? '#20aedc' : '#fff',
                  color: selected ? '#fff' : '#6a7282',
                  border: selected ? '1px solid #20aedc' : '1px solid #e5e7eb',
                  '&:hover': {
                    bgcolor: selected ? '#1c9ecf' : '#f8fafc',
                    borderColor: selected ? '#20aedc' : '#d0d5dd',
                  },
                }}
              >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {filterIcon}
                  {label}
                  <Box
                    component="span"
                    sx={{
                      minWidth: 16,
                      height: 16,
                      px: 0.35,
                      borderRadius: '999px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '0.167px',
                      bgcolor: selected ? 'rgba(255, 255, 255, 0.25)' : '#f3f4f6',
                      color: selected ? '#fff' : countColor,
                    }}
                  >
                    {counts[filterKey]}
                  </Box>
                </Box>
              </Button>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ pt: '12px', borderTop: '1px solid #f1f5f9', display: 'flex', minWidth: 0 }}>
        <Box
          sx={{
            width: yAxisWidth,
            flexShrink: 0,
            position: 'relative',
            height: plotHeight,
            borderRight: '1px solid #f8fafc',
          }}
        >
          {yTicks.map((tick) => {
            const bottom = chartBottom + (tick / scaleMax) * chartHeight;
            return (
              <Typography
                key={`ylabel-${tick}`}
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 4,
                  bottom: bottom - 7,
                  textAlign: 'right',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  color: '#98a2b3',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {tick}%
              </Typography>
            );
          })}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, overflowX: 'auto', pb: 0.2 }}>
          <Box sx={{ minWidth: Math.max(320, chartMinWidth - yAxisWidth), height: plotHeight, position: 'relative' }}>
            {yTicks.map((tick) => {
              const bottom = chartBottom + (tick / scaleMax) * chartHeight;
              return (
                <Box
                  key={`grid-${tick}`}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom,
                    borderTop: '1px dashed #edf2f7',
                    pointerEvents: 'none',
                  }}
                />
              );
            })}

            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 2,
                bottom: 0,
                height: plotHeight,
                display: 'grid',
                gridTemplateColumns: columns,
                gap: 0.8,
              }}
            >
              {visibleBars.map((bar) => {
                const percent = normalizeBarPercent(bar.percent);
                const clamped = Math.max(0, Math.min(scaleMax, percent));
                return (
                <ButtonBase
                  key={bar.id}
                  type="button"
                  onClick={() =>
                    setSelectedBar((prev) => (prev?.id === bar.id ? null : bar))
                  }
                  aria-label={`Open details for ${bar.label}`}
                  sx={{
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderRadius: '10px',
                    px: 0.25,
                    '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)' },
                  }}
                >
                  <Box
                    sx={{
                      height: chartHeight,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 36,
                      mx: 'auto',
                      height: `${Math.max(8, (clamped / scaleMax) * chartHeight)}px`,
                      borderRadius: '8px 8px 2px 2px',
                      bgcolor: getStatusColor(bar.status),
                    }}
                  />
                </Box>
              <Typography
                sx={{
                      mt: 0.35,
                      height: chartBottom - 4,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                  textAlign: 'center',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.75rem',
                  color: '#98a2b3',
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  wordBreak: 'break-word',
                  lineHeight: 1.15,
                      width: '100%',
                }}
                title={bar.label}
              >
                {bar.label}
              </Typography>
                </ButtonBase>
              );
            })}
            </Box>
          </Box>
        </Box>
      </Box>

      {selectedBar ? (
        <Box ref={detailPanelRef} sx={{ borderTop: '2px solid #f1f5f9', pt: '2px', bgcolor: '#fff' }}>
          <Box
            component="header"
            sx={{
              bgcolor: '#e8f4f8',
              borderBottom: '1px solid #f8fafc',
              px: '24px',
              py: '16px',
              minHeight: 76,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: '1 1 auto' }}>
            <Typography
                id="micronutrient-detail-title"
              sx={{
                fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '16px',
                  lineHeight: '20px',
                  letterSpacing: '-0.3125px',
                  color: '#1e2939',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
                {selectedBar.label}
              </Typography>
              <Box
                sx={{
                  flexShrink: 0,
                  bgcolor: 'rgba(32, 174, 220, 0.09)',
                  borderRadius: 9999,
                  px: '18px',
                  py: '2px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '12px',
                    lineHeight: '15px',
                    letterSpacing: '0.1172px',
                    color: '#303030',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {GROUP_CATEGORY_LABEL[resolvedGroup(selectedBar)]}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '14px',
                  height: 32,
                  px: '12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                  <circle cx="4" cy="4" r="4" fill={getStatusColor(selectedBar.status)} />
                </svg>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '-0.1504px',
                    color: getStatusColor(selectedBar.status),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {statusHeadline(selectedBar.status)}
            </Typography>
              </Box>
              <IconButton
                type="button"
                onClick={() => setSelectedBar(null)}
                aria-label="Close"
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: '#fff',
                  borderRadius: '50%',
                  '&:hover': { bgcolor: '#f8fafc' },
                }}
              >
                <CloseRounded sx={{ fontSize: 18, color: '#64748b' }} />
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              pt: '20px',
              pb: 3,
              px: '24px',
              bgcolor: '#fff',
            }}
          >
            <MicronutrientDetailPanel bar={selectedBar} />
          </Box>
      </Box>
      ) : null}
    </Paper>
  );
};
